import { Button, Flex, Modal, Typography } from '@iheartjane/reefer';
import { Form, useForm } from '@iheartjane/reefer-hook-form';
import { useEffect, useState } from 'react';

interface CloudflareIntegrationFormData {
  account_id: string;
  api_token: string;
  deploy_robots_txt: boolean;
  name: string;
  route_pattern: string;
  zone_id: string;
}

export interface CloudflareIntegrationSubmitData extends Omit<
  CloudflareIntegrationFormData,
  'api_token'
> {
  api_token?: string;
}

interface CloudflareIntegrationModalProps {
  initialValues?: {
    account_id: string;
    api_token_masked: string | null;
    deploy_robots_txt: boolean;
    name: string;
    route_pattern: string;
    zone_id: string;
  };
  isDeployed?: boolean;
  isEditing: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CloudflareIntegrationSubmitData) => Promise<void>;
  serverError?: string | null;
}

const HEX_32_PATTERN = /^[a-f0-9]{32}$/i;

export const CloudflareIntegrationModal = ({
  isOpen,
  isEditing,
  isDeployed = false,
  initialValues,
  onClose,
  onSubmit,
  serverError,
}: CloudflareIntegrationModalProps) => {
  const [isTokenLocked, setIsTokenLocked] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formMethods = useForm<CloudflareIntegrationFormData>({
    defaultValues: {
      name: initialValues?.name || '',
      account_id: initialValues?.account_id || '',
      zone_id: initialValues?.zone_id || '',
      route_pattern: initialValues?.route_pattern || '',
      deploy_robots_txt: initialValues?.deploy_robots_txt ?? true,
      api_token: '',
    },
  });

  useEffect(() => {
    setIsTokenLocked(isEditing);
  }, [isEditing]);

  useEffect(() => {
    if (serverError) {
      setIsTokenLocked(false);
      formMethods.setError('api_token', { message: serverError });
    }
  }, [serverError, formMethods]);

  const handleSubmit = async (data: CloudflareIntegrationFormData) => {
    setIsSubmitting(true);
    try {
      const submitData: CloudflareIntegrationSubmitData = {
        name: data.name,
        account_id: data.account_id,
        zone_id: data.zone_id,
        route_pattern: data.route_pattern,
        deploy_robots_txt: data.deploy_robots_txt,
      };

      if (!isEditing || !isTokenLocked) {
        submitData.api_token = data.api_token;
      }

      await onSubmit(submitData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      appId="parent"
      open={isOpen}
      onRequestClose={onClose}
      overlayClose
      isLayered
      variant="standard-dialogue"
      overflow="auto"
    >
      <Form.BaseForm
        formMethods={formMethods}
        name="cloudflare-integration-form"
        // @ts-expect-error - FormProps types onSubmit as (data: FormData) instead of generic type
        onSubmit={handleSubmit}
      >
        <Modal.Header
          nested
          title={
            isEditing
              ? 'Edit Cloudflare integration'
              : 'Add Cloudflare integration'
          }
          actions={
            <Form.SubmitButton
              label="Save"
              variant="primary"
              loading={isSubmitting}
            />
          }
        />
        <Modal.Content>
          <Flex flexDirection="column" gap={24}>
            <Typography variant="h2-semibold" branded>
              Cloudflare credentials
            </Typography>

            {isEditing && isTokenLocked ? (
              <Flex alignItems="center" justifyContent="space-between">
                <Flex flexDirection="column" gap={4}>
                  <Typography variant="body-semibold">API Token</Typography>
                  <Typography color="grays-mid">
                    {initialValues?.api_token_masked || '••••••••'}
                  </Typography>
                </Flex>
                <Button
                  label="Change token"
                  variant="tertiary"
                  onClick={() => {
                    setIsTokenLocked(false);
                    formMethods.setValue('api_token', '', {
                      shouldDirty: true,
                    });
                  }}
                />
              </Flex>
            ) : (
              <Form.TextField
                label="API Token"
                name="api_token"
                type="password"
                required
                helperText="API token with Workers Scripts:Edit and Workers Routes:Edit permissions"
              />
            )}

            <Form.TextField
              label="Zone ID"
              name="zone_id"
              required
              validate={(value: string) =>
                HEX_32_PATTERN.test(value) ||
                'Must be a 32-character hex string'
              }
              helperText="32-character Cloudflare zone ID"
            />

            <Form.TextField
              label="Account ID"
              name="account_id"
              required
              validate={(value: string) =>
                HEX_32_PATTERN.test(value) ||
                'Must be a 32-character hex string'
              }
              helperText="32-character Cloudflare account ID"
            />

            <Modal.ContentDivider />

            <Typography variant="h2-semibold" branded>
              Worker configuration
            </Typography>

            <Form.TextField
              label="Worker Name"
              name="name"
              helperText="The identifier for this worker in Cloudflare (e.g., 'production-menu' or 'staging-menu')"
            />

            <Form.TextField
              label="Route Pattern"
              name="route_pattern"
              required
              placeholder="yourdomain.com/shop*"
              helperText="The URL pattern that tells Cloudflare which pages on your website should display your menu. For example, yourdomain.com/shop* means any page under the /shop path on your site will show your menu. Routes are automatically created for both the base domain and www."
            />

            <Modal.ContentDivider />

            <Typography variant="h2-semibold" branded>
              SEO configuration
            </Typography>

            <Flex flexDirection="column" gap={8}>
              <Form.CheckboxField
                name="deploy_robots_txt"
                label="Deploy robots.txt worker"
                defaultChecked={initialValues?.deploy_robots_txt ?? true}
                disabled={isDeployed}
              />
              <Typography color="grays-mid">
                The robots.txt worker appends a sitemap directive to your
                site&apos;s robots.txt, helping search engines discover the
                menu. Disabling this will make your menu pages more difficult to
                index, unless you manually add the menu sitemap to your
                robots.txt file.
              </Typography>
            </Flex>
          </Flex>
        </Modal.Content>
      </Form.BaseForm>
    </Modal>
  );
};
